import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import { useAvailability } from '@search/index'
import { SaveSearchPayload } from '@typescript'

/**
 * Hook to synchronize URL query parameters with availability context.
 * This ensures that when a user loads a URL with saved search parameters,
 * the same availability API calls are triggered as when clicking "Done"
 * in the calendar dropdown.
 */
export const useQueryParamsSyncWithAvailability = (): void => {
  const location = useLocation()
  const { setSavedSearch, savedSearch } = useAvailability()

  useEffect(() => {
    // Parse URL query parameters
    const searchParams = new URLSearchParams(location.search)

    // Check if we have search parameters that indicate a saved search
    if (searchParams.has('searchLocation') || searchParams.has('searchDateStart') || searchParams.has('searchDateEnd') || searchParams.has('searchName')) {
      const newSearchLocation = searchParams.get('searchLocation')
      const newSearchDateStart = searchParams.get('searchDateStart')
      const newSearchDateEnd = searchParams.get('searchDateEnd')
      const newSearchName = searchParams.get('searchName')

      // Check if the current saved search parameters match the new ones from URL
      if (savedSearch && typeof savedSearch === 'object') {
        // Create a normalized object with search parameters from URL
        const newSearchParams = {
          searchLocation: newSearchLocation || null,
          searchDateStart: newSearchDateStart || null,
          searchDateEnd: newSearchDateEnd || null,
          searchName: newSearchName || null,
        }

        const currentSearchParams = {
          searchLocation: savedSearch.searchLocation || null,
          searchDateStart: savedSearch.searchDateStart || null,
          searchDateEnd: savedSearch.searchDateEnd || null,
          searchName: savedSearch.searchName || null,
        }

        // Use lodash's isEqual for deep comparison of search parameters
        // This handles both full objects and minimal objects from tests
        // By normalizing nulls, we ensure consistent comparison
        if (isEqual(currentSearchParams, newSearchParams)) {
          // Don't update if the parameters match what's already in saved search
          return
        }
      }

      // Construct saved search payload from URL parameters
      const savedSearchPayload: SaveSearchPayload = {
        name: newSearchName || 'Saved Search', // Required field
        searchLocation: newSearchLocation || undefined,
        searchDateStart: newSearchDateStart || undefined,
        searchDateEnd: newSearchDateEnd || undefined,
        searchName: newSearchName || undefined,
      }

      // Set the saved search in the availability context
      setSavedSearch(savedSearchPayload)
    }
  }, [location.search, setSavedSearch, savedSearch])
}
